/**
 * 정확한 분수. 부동소수 오차 없이 나누고 곱하려고 bigint 로 둔다.
 * 엔진의 금액·수량 계산은 모두 이 분수로 하고, 반올림은 화면에 그릴 때 한 번만 한다(rounding.ts).
 */
export type Rational = { n: bigint; d: bigint }
/** 밖으로 내보내는 모양. JSON 에 그대로 담기게 문자열 */
export type ExactValue = { numerator: string; denominator: string }

const absolute = (v: bigint) => (v < 0n ? -v : v)

export function rational(n: bigint, d = 1n): Rational {
  if (d === 0n) throw Error('Invalid exact denominator')
  let a = absolute(n),
    b = absolute(d)
  while (b) {
    const next = a % b
    a = b
    b = next
  }
  const gcd = a || 1n
  return { n: (d < 0n ? -n : n) / gcd, d: absolute(d) / gcd }
}

/** 입력 숫자의 10진 표기 그대로 분수로 (0.1 은 1/10, 1e-7 도 지수를 풀어서) */
export function decimalToRational(value: number): Rational {
  const [mantissa, exponent = '0'] = String(value).toLowerCase().split('e')
  const [whole, fraction = ''] = mantissa!.split('.')
  const power = Number(exponent) - fraction.length
  const n = BigInt(`${whole}${fraction}`)
  return power >= 0 ? rational(n * 10n ** BigInt(power)) : rational(n, 10n ** BigInt(-power))
}

export const add = (a: Rational, b: Rational) => rational(a.n * b.d + b.n * a.d, a.d * b.d)
export const sub = (a: Rational, b: Rational) => rational(a.n * b.d - b.n * a.d, a.d * b.d)
export const mul = (a: Rational, b: Rational) => rational(a.n * b.n, a.d * b.d)
export const div = (a: Rational, b: Rational) => rational(a.n * b.d, a.d * b.n)
export const ZERO = rational(0n)
export const ONE = rational(1n)
export const integer = (v: number | bigint) => rational(BigInt(v))
export const sum = (values: Rational[]) => values.reduce(add, ZERO)
/** a 와 b 의 크기 비교: 음수면 a < b */
export const compare = (a: Rational, b: Rational) => {
  const diff = a.n * b.d - b.n * a.d
  return diff < 0n ? -1 : diff > 0n ? 1 : 0
}
/** 0 이상 분수의 내림 정수 (충전 개수처럼 '다 채운 것만' 셀 때) */
export const floorNonNegative = (v: Rational) => v.n / v.d
/** 0 이상 분수의 올림 정수 (회수 수량처럼 '넘으려면 몇 개' 셀 때) */
export const ceilNonNegative = (v: Rational) => (v.n + v.d - 1n) / v.d

export const toExact = (v: Rational | null): ExactValue | null =>
  v ? { numerator: String(v.n), denominator: String(v.d) } : null

/** 밖으로 내보낸 값을 다시 분수로 (저장된 계산 결과를 이어 계산할 때) */
export const fromExact = (value: ExactValue) =>
  rational(BigInt(value.numerator), BigInt(value.denominator))

/** 정확한 분수를 화면에 쓸 숫자로. 표시용이며 다시 계산에 넣지 않는다 */
export function exactToNumber(value: ExactValue): number {
  return Number(value.numerator) / Number(value.denominator)
}

export const rationalToNumber = (value: Rational) => Number(value.n) / Number(value.d)
